import classNames from 'classnames';
import React from 'react';
import { Box } from '../../box/Box';
import type { ElementProps } from '../../element/Element';
import { Element } from '../../element/Element';
import type { TimelineOrientation } from '../Timeline.types';
import styles from './Timeline.SummaryBlock.module.scss';

interface TimelineSummaryBlockProps extends ElementProps {
  /** Position from the top (vertical) or left (horizontal) in pixels */
  offsetPx?: number;
  /** Size in pixels (height for vertical, width for horizontal) */
  sizePx?: number;
  /** Timeline orientation */
  orientation?: TimelineOrientation;
  /** Whether this block is currently active/selected */
  active?: boolean;
}

export type { TimelineSummaryBlockProps };

/**
 * Container component for summary blocks on the timeline
 */
const SummaryContainer = React.forwardRef<HTMLDivElement, TimelineSummaryBlockProps>(
  (props, ref) => {
    const { offsetPx, orientation = 'vertical', active, style, ...rest } = props;

    return (
      <Element
        ref={ref}
        {...rest}
        style={{
          top: orientation === 'vertical' ? offsetPx : undefined,
          left: orientation === 'horizontal' ? offsetPx : undefined,
          ...style,
        }}
        className={classNames(styles.container, { [styles.active]: active }, props.className)}
      >
        {props.children}
      </Element>
    );
  }
);
SummaryContainer.displayName = 'Timeline.Lane.SummaryBlock';

interface SummaryContentProps extends ElementProps {
  /** Size in pixels (height for vertical, width for horizontal) */
  sizePx?: number;
  /** Timeline orientation */
  orientation?: TimelineOrientation;
  /** Color accent for the block */
  color?: string;
  /** Size of block container in UI */
  size?: 'small' | 'medium';
}

export type { SummaryContentProps };

const SummaryContent = React.forwardRef<HTMLDivElement, SummaryContentProps>((props, ref) => {
  const {
    size = 'medium',
    sizePx,
    orientation = 'vertical',
    color = 'rgba(0, 0, 0, 0.1)',
    style,
    ...rest
  } = props;

  return (
    <Box.Column
      gap="small"
      ref={ref}
      {...rest}
      style={
        {
          height: orientation === 'vertical' ? `${sizePx}px` : undefined,
          width: orientation === 'horizontal' ? `${sizePx}px` : undefined,
          backgroundColor: color,
          '--summary-bg-color': color, // Add CSS variable for the fade effect
          ...style,
        } as React.CSSProperties
      }
      className={classNames(styles.content, { [styles[`size-${size}`]]: !!size }, props.className)}
    >
      {props.children}
    </Box.Column>
  );
});
SummaryContent.displayName = 'Timeline.Lane.SummaryBlock.Content';

/**
 * A summary block for the timeline - displays aggregated information
 * about activities in a specific time period.
 */
const TimelineSummaryBlock = Object.assign(SummaryContainer, {
  /** SummaryContent display of content */
  Content: SummaryContent,
});

export default TimelineSummaryBlock;

TimelineSummaryBlock.displayName = 'Timeline.SummaryBlock';
TimelineSummaryBlock.Content.displayName = 'Timeline.SummaryBlock.Content';
